import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * A lightweight, reusable "empty state" UI component that displays an image, title,
 * description and an optional action button when a view has no results to show.
 * Designed to be used anywhere a consistent empty-result representation is needed.
 * Key method:
 *   - onActionClick(): emits the actionClicked output and calls the optional actionClick callback (for backward compatibility).
 * Lifecycle hooks:
 *   - This standalone component does not implement Angular lifecycle hooks itself.
 * @component
 * @selector app-empty-result
 * @example
 *   <app-empty-result
 *     [image]="'/assets/empty.png'"
 *     [title]="'No results found'"
 *     [description]="'Try adjusting your filters or search terms.'"
 *     [actionText]="'Retry'"
 *     [showAction]="true"
 *     [actionClick]="legacyHandler"
 *     (actionClicked)="onRetry()">
 *   </app-empty-result>
 * @input {string} image - URL or path to an illustrative image displayed in the empty state.
 * @input {string} title - Short heading describing the empty state.
 * @input {string} description - Additional explanatory text shown beneath the title.
 * @input {string} actionText - Label displayed on the optional action button.
 * @input {boolean} showAction - Controls whether the action button is rendered.
 * @input {(() => void) | null} actionClick - Optional synchronous callback invoked when the action is clicked (provided for backward compatibility).
 * @output {EventEmitter<void>} actionClicked - Event emitted when the action button is clicked; preferred way for parent components to respond to the action.
 */
@Component({
  selector: 'app-empty-result',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './empty-result.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./empty-result.component.scss'],
})
export class EmptyResultComponent {
  @Input() image = '';
  @Input() title = '';
  @Input() description = '';
  @Input() actionText = '';
  @Input() showAction = false;
  @Input() actionClick: (() => void) | null = null;
  /** `data-testid` stem: emits `<testId>`, `<testId>-title`, `<testId>-description` and
   * `<testId>-action-button`. Defaults to the generic `empty-result`. */
  @Input() testId = 'empty-result';

  @Output() actionClicked = new EventEmitter<void>();

  onActionClick(): void {
    // Emit the event for parent components to handle
    this.actionClicked.emit();

    // Also call the actionClick function if provided (for backward compatibility)
    if (this.actionClick) {
      this.actionClick();
    }
  }
}
